import { Fragment, type ReactNode, useId } from "react";
import type { IntakeField, IntakeFormSchema, IntakeValues } from "@/api/ticket-intake";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel, FieldLegend, FieldSet } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/utils";
import { type IntakeLookup, IntakeLookupControl } from "./intake-lookup";
import { useIntakeMessages } from "./intake-messages";
import { fieldVisible, isFilled, matchesCondition } from "./intake-model";

export function DynamicIntakeFields({
  schema,
  values,
  context = {},
  disabled,
  errors = {},
  lookup,
  onChange,
}: {
  schema: IntakeFormSchema;
  values: IntakeValues;
  context?: IntakeValues;
  disabled?: boolean;
  errors?: Record<string, string>;
  lookup?: IntakeLookup;
  onChange: (key: string, value: unknown) => void;
}) {
  const all = { ...context, ...values };
  const fields = schema.fields.filter((field) => fieldVisible(field, all)).sort((left, right) => (left.order ?? 0) - (right.order ?? 0));
  return (
    <FieldGroup className="grid sm:grid-cols-2">
      {fields.map((field, index) => (
        <Fragment key={field.key}>
          {field.group && fields[index - 1]?.group !== field.group ? (
            <h4 className="min-w-0 break-words text-sm font-medium sm:col-span-2">{field.group}</h4>
          ) : null}
          <DynamicField
            field={field}
            value={values[field.key]}
            all={all}
            disabled={disabled}
            error={errors[field.key]}
            lookup={lookup}
            onChange={(value) => onChange(field.key, value)}
          />
        </Fragment>
      ))}
    </FieldGroup>
  );
}

function DynamicField({
  field,
  value,
  all,
  disabled,
  error,
  lookup,
  onChange,
}: {
  field: IntakeField;
  value: unknown;
  all: IntakeValues;
  disabled?: boolean;
  error?: string;
  lookup?: IntakeLookup;
  onChange: (value: unknown) => void;
}) {
  const m = useIntakeMessages();
  const id = useId();
  const required = field.required || (field.requiredWhen ? matchesCondition(field.requiredWhen, all) : false);
  const dependencies = field.dependsOn ?? [];
  const dependencyMissing = dependencies.some((key) => !isFilled(all[key]));
  const scope = JSON.stringify([field.source, ...dependencies.map((key) => all[key])]);
  const descriptionId = field.helpText || error ? `${id}-help` : undefined;
  const common = { id, disabled, "aria-invalid": Boolean(error), "aria-describedby": descriptionId };
  const inlineMultiselect = field.type === "multiselect" && !field.source;
  const label = (
    <>
      {field.label}
      {field.constraints?.currency ? ` (${field.constraints.currency})` : ""}
      {required ? <span className="text-destructive"> *</span> : null}
    </>
  );
  let control: ReactNode;
  if (field.source) {
    control = (
      <IntakeLookupControl
        capability={field.source}
        inputId={id}
        searchLabel={`${m.search} ${field.label}`}
        invalid={Boolean(error)}
        required={required}
        descriptionId={descriptionId}
        multiple={field.type === "multiselect"}
        disabled={disabled || dependencyMissing}
        lookup={lookup}
        scope={scope}
        value={value}
        onChange={(option) =>
          onChange(field.type === "multiselect" ? (option ? [...new Set([...(Array.isArray(value) ? value : []), option.value])] : []) : option?.value)
        }
        onRemove={(item) => onChange(Array.isArray(value) ? value.filter((selected) => selected !== item) : [])}
      />
    );
  } else if (field.type === "boolean") {
    control = (
      <Select
        disabled={disabled}
        value={typeof value === "boolean" ? String(value) : "__none__"}
        onValueChange={(next) => onChange(next === "__none__" ? undefined : next === "true")}
      >
        <SelectTrigger {...common} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value="__none__">{m.select}</SelectItem>
            <SelectItem value="true">{m.yes}</SelectItem>
            <SelectItem value="false">{m.no}</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
    );
  } else if (field.type === "select" || field.type === "entitySelect") {
    control = (
      <Select
        disabled={disabled}
        value={typeof value === "string" && value ? value : "__none__"}
        onValueChange={(next) => onChange(next === "__none__" ? undefined : next)}
      >
        <SelectTrigger {...common} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value="__none__">{m.select}</SelectItem>
            {(field.options ?? []).map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    );
  } else if (field.type === "multiselect") {
    const selected = Array.isArray(value) ? value : [];
    control = (
      <FieldSet id={id} aria-invalid={Boolean(error)} aria-describedby={descriptionId} disabled={disabled}>
        <FieldLegend variant="label">{label}</FieldLegend>
        <FieldGroup>
          {(field.options ?? []).map((option, index) => (
            <Field orientation="horizontal" key={option.value} data-disabled={disabled}>
              <Checkbox
                id={`${id}-option-${index}`}
                checked={selected.includes(option.value)}
                disabled={disabled}
                aria-invalid={Boolean(error)}
                onCheckedChange={(checked) => onChange(checked ? [...selected, option.value] : selected.filter((item) => item !== option.value))}
              />
              <FieldLabel htmlFor={`${id}-option-${index}`} className="min-w-0 break-words">
                {option.label}
              </FieldLabel>
            </Field>
          ))}
          {!field.options?.length ? <FieldDescription>{m.noResults}</FieldDescription> : null}
        </FieldGroup>
      </FieldSet>
    );
  } else if (field.type === "textarea") {
    control = (
      <Textarea {...common} maxLength={field.constraints?.maxLength} rows={4} value={String(value ?? "")} onChange={(event) => onChange(event.target.value)} />
    );
  } else {
    const numeric = field.type === "money" || field.type === "number";
    control = (
      <Input
        {...common}
        max={numeric ? field.constraints?.max : undefined}
        min={numeric ? field.constraints?.min : undefined}
        maxLength={numeric ? undefined : field.constraints?.maxLength}
        step={numeric ? "any" : undefined}
        type={numeric ? "number" : field.type === "date" ? "date" : "text"}
        value={String(value ?? "")}
        onChange={(event) => onChange(field.type === "number" ? (event.target.value === "" ? undefined : Number(event.target.value)) : event.target.value)}
      />
    );
  }
  return (
    <Field data-invalid={Boolean(error)} data-disabled={disabled} className={cn("min-w-0", (field.type === "textarea" || field.source) && "sm:col-span-2")}>
      {!inlineMultiselect ? (
        <FieldLabel htmlFor={id} className="min-w-0 break-words">
          {label}
        </FieldLabel>
      ) : null}
      {control}
      {descriptionId ? (
        <div id={descriptionId} className="flex flex-col gap-1">
          {field.helpText ? <FieldDescription>{field.helpText}</FieldDescription> : null}
          {error ? <FieldError>{error}</FieldError> : null}
        </div>
      ) : null}
    </Field>
  );
}
